/**
 * AttendanceOps.jsx — Puantaj Operasyon Sayfası (HR / OWNER)
 * Tab sistemi: Puantaj | Mesai Planı | Departman | İstisna | Bordro | Ayarlar
 */
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Toaster, toast } from 'react-hot-toast';
import api from '../../../api/axios';
import AttendanceDashboard from '../components/AttendanceDashboard';
import AttendanceFilterBar from '../components/AttendanceFilterBar';
import AttendanceTable from '../components/AttendanceTable';
import ScheduleManager from '../components/ScheduleManager';
import DepartmentAssignment from '../components/DepartmentAssignment';
import EmployeeOverride from '../components/EmployeeOverride';
import PayrollSummary from '../components/PayrollSummary';
import TimesheetPeriods from '../components/TimesheetPeriods';
import HolidayCalendarPanel from '../../../components/HolidayCalendarPanel';
import PayCodeApprovalPanel from '../../../components/PayCodeApprovalPanel';
import PayrollExportProfilePanel from '../../../components/PayrollExportProfilePanel';
import useAttendanceFilters from '../hooks/useAttendanceFilters';
import useScheduleData from '../hooks/useScheduleData';
import { computeStats } from '../utils/attendanceHelpers';

const TAB_KEYS = {
  ATTENDANCE: 'ATTENDANCE',
  SCHEDULES: 'SCHEDULES',
  DEPARTMENTS: 'DEPARTMENTS',
  OVERRIDES: 'OVERRIDES',
  PAYROLL: 'PAYROLL',
  SETTINGS: 'SETTINGS',
};

const TABS = (t) => [
  { key: TAB_KEYS.ATTENDANCE, label: t('operations_time_tab_puantaj', 'Puantaj'), icon: '⏱' },
  { key: TAB_KEYS.SCHEDULES, label: t('operations_time_tab_schedules', 'Mesai Planı'), icon: '◷' },
  { key: TAB_KEYS.DEPARTMENTS, label: t('operations_time_tab_departments', 'Departman'), icon: '🏢' },
  { key: TAB_KEYS.OVERRIDES, label: t('operations_time_tab_overrides', 'İstisna'), icon: '👤' },
  { key: TAB_KEYS.PAYROLL, label: t('operations_time_tab_reports', 'Bordro'), icon: '▤' },
  { key: TAB_KEYS.SETTINGS, label: t('operations_time_tab_settings', 'Ayarlar'), icon: '⚙' },
];

const AttendanceOps = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';
  const [activeTab, setActiveTab] = useState(TAB_KEYS.ATTENDANCE);

  // Puantaj verisi
  const [records, setRecords] = useState([]);
  const [attLoading, setAttLoading] = useState(true);
  const [totalEmployees, setTotalEmployees] = useState(0);

  // Filtre hook'u
  const {
    filters,
    draft,
    activeFilterMenu,
    openFilterMenu,
    applyFilters,
    resetFilters,
    hasActiveFilter,
    setDraftField,
    buildApiParams,
  } = useAttendanceFilters();

  // Schedule hook'u
  const {
    templates,
    departments,
    departmentAssignments,
    employees,
    employeeOverrides,
    loading: scheduleLoading,
    saving: scheduleSaving,
    refresh: refreshSchedules,
    fetchTemplates,
    saveDepartmentAssignments,
    setDepartmentAssignments,
  } = useScheduleData({ language: i18n.language });

  // Yan veriler
  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const res = await api.get('/employee/list?status=ACTIVE');
        setTotalEmployees((res.data || []).length);
      } catch { /* silent */ }
    };
    fetchMeta();
  }, []);

  // Puantaj verisi çekme
  const fetchAttendance = useCallback(async () => {
    setAttLoading(true);
    try {
      const params = buildApiParams();
      const res = await api.get('/attendance/list', { params });
      setRecords(res.data || []);
    } catch {
      toast.error(t('err_fetch_attendance', 'Puantaj verileri çekilemedi.'));
    } finally {
      setAttLoading(false);
    }
  }, [buildApiParams, t]);

  useEffect(() => {
    fetchAttendance();
  }, [filters, fetchAttendance]);

  // Toplu işlemler
  const handleBulkApprove = async (ids) => {
    try {
      await api.put('/attendance/approve', { ids, action: 'APPROVE' });
      toast.success(t('attendance_bulk_approved', 'Seçilen kayıtlar onaylandı.'));
      fetchAttendance();
    } catch {
      toast.error(t('err_action_failed', 'İşlem başarısız.'));
    }
  };

  const handleExport = async (format) => {
    try {
      const params = buildApiParams();
      params.format = format;
      const res = await api.get('/attendance/export', { params, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `puantaj-${filters.from_date || 'all'}.${format === 'csv' ? 'csv' : 'xlsx'}`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(t('attendance_export_success', 'Dışa aktarım tamamlandı.'));
    } catch {
      toast.error(t('err_export_failed', 'Dışa aktarım başarısız.'));
    }
  };

  const stats = computeStats(records);

  return (
    <div className="space-y-6 animate-in fade-in duration-500" dir={isArabic ? 'rtl' : 'ltr'}>
      <Toaster position={isArabic ? 'top-left' : 'top-right'} />

      {/* Tab Navigasyonu */}
      <nav className="flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
        {TABS(t).map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-bold transition-all ${
              activeTab === tab.key
                ? 'bg-cyan-600 text-white shadow-md'
                : 'text-slate-600 hover:bg-cyan-50 hover:text-cyan-700'
            }`}
          >
            <span aria-hidden="true">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </nav>

      {/* ====== PUANTAJ SEKMESİ ====== */}
      {activeTab === TAB_KEYS.ATTENDANCE && (
        <div className="space-y-6">
          <AttendanceDashboard stats={stats} role="HR" totalEmployees={totalEmployees} />

          <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
            <AttendanceFilterBar
              filters={filters}
              draft={draft}
              activeFilterMenu={activeFilterMenu}
              hasActiveFilter={hasActiveFilter}
              openFilterMenu={openFilterMenu}
              applyFilters={applyFilters}
              resetFilters={resetFilters}
              setDraftField={setDraftField}
              departments={departments}
              onExport={handleExport}
              showExport
            />
          </div>

          <AttendanceTable
            records={records}
            loading={attLoading}
            showEmployee
            showApproval
            showCheckbox
            onBulkApprove={handleBulkApprove}
          />
        </div>
      )}

      {/* ====== MESAİ PLANI SEKMESİ ====== */}
      {activeTab === TAB_KEYS.SCHEDULES && (
        <ScheduleManager
          templates={templates}
          loading={scheduleLoading}
          onRefresh={fetchTemplates}
        />
      )}

      {/* ====== DEPARTMAN SEKMESİ ====== */}
      {activeTab === TAB_KEYS.DEPARTMENTS && (
        <DepartmentAssignment
          departments={departments}
          schedules={templates}
          assignments={departmentAssignments}
          saving={scheduleSaving}
          onAssignmentChange={(deptId, scheduleId) =>
            setDepartmentAssignments((prev) => ({ ...prev, [deptId]: scheduleId }))
          }
          onSave={() => saveDepartmentAssignments(departmentAssignments)}
        />
      )}

      {/* ====== İSTİSNA SEKMESİ ====== */}
      {activeTab === TAB_KEYS.OVERRIDES && (
        <EmployeeOverride
          employees={employees}
          schedules={templates}
          overrides={employeeOverrides}
          loading={scheduleLoading}
          onRefresh={refreshSchedules}
        />
      )}

      {/* ====== BORDRO SEKMESİ ====== */}
      {activeTab === TAB_KEYS.PAYROLL && (
        <div className="space-y-6">
          <PayrollSummary filters={filters} />
          <PayrollExportProfilePanel t={t} />
        </div>
      )}

      {/* ====== AYARLAR SEKMESİ ====== */}
      {activeTab === TAB_KEYS.SETTINGS && (
        <div className="space-y-6">
          <TimesheetPeriods />
          <HolidayCalendarPanel t={t} />
          <PayCodeApprovalPanel t={t} />
        </div>
      )}
    </div>
  );
};

export default AttendanceOps;